import React from "react";
import {
  Box,
  Avatar,
  Typography,
  Popper,
  ClickAwayListener,
  Paper,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useSidebar } from "./SidebarContext";
import type { IThemeState } from "../../types/theme";
import { tokens } from "../../theme";

interface ProfileSectionProps {
  user: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_picture?: string | null;
  } | null;
  theme: IThemeState;
  onLogout?: () => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  theme,
  onLogout,
}) => {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode, theme);
  const { isCollapsed } = useSidebar();
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
  const [profileAnchorEl, setProfileAnchorEl] =
    React.useState<HTMLElement | null>(null);

  if (!user) return null;

  const displayName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || "User";

  const handleCloseProfileMenu = () => {
    setProfileMenuOpen(false);
    setProfileAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={(e) => {
          setProfileMenuOpen(!profileMenuOpen);
          setProfileAnchorEl(e.currentTarget as HTMLElement);
        }}
        sx={{
          mt: "auto",
          mx: isCollapsed ? 1 : 2,
          mb: 2,
          p: isCollapsed ? 1 : 2,
          border: "1px solid",
          borderRadius: 1.5,
          borderColor: colors.neutral[800] || "#333",
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: colors.neutral[800],
            borderColor: colors.primary[400],
          },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: "0.9rem",
            fontWeight: 600,
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        {!isCollapsed && (
          <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "0.9rem",
              }}
            >
              {displayName}
            </Typography>
          </Box>
        )}
      </Box>

      <Popper
        open={profileMenuOpen}
        anchorEl={profileAnchorEl}
        placement="top-end"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleCloseProfileMenu}>
          <Paper>
            <Box sx={{ p: 1, minWidth: 150 }}>
              <MenuItem onClick={handleCloseProfileMenu}>Profile</MenuItem>
              <MenuItem
                onClick={() => {
                  onLogout?.();
                  handleCloseProfileMenu();
                }}
              >
                Logout
              </MenuItem>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
